import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { fdateShort } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader } from "@/components/Common";
import { toast } from "sonner";
import { Check, Heart, PartyPopper, Users } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1757682066255-dcb508d6c285?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function PublicRSVP() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("attending");
  const [answers, setAnswers] = useState({});
  const [members, setMembers] = useState([]); // per-person household state
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/rsvp/${token}`)
      .then(({ data }) => {
        setData(data);
        setStatus(data.guest.rsvp_status === "declined" ? "declined" : "attending");
        const list = (data.household_members || []).map((m) => ({
          ...m,
          rsvp_status: m.rsvp_status === "declined" ? "declined" : "attending",
          meal_choice: m.meal_choice || "",
          allergies: m.allergies || "",
        }));
        setMembers(list);
      })
      .catch(() => setData(false));
  }, [token]);

  if (data === null) return <div className="min-h-screen bg-background"><Loader /></div>;
  if (data === false) return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center"><h1 className="font-serif text-2xl">Lien invalide</h1>
        <p className="text-stone-500 mt-2">Ce lien de réponse n'est plus valide.</p></div></div>
  );

  const w = data.wedding;
  const form = data.form;
  const mealQ = (form?.questions || []).find((q) => q.type === "meal");
  const isHousehold = members.length > 1;

  const setMember = (id, k, v) => setMembers((prev) => prev.map((m) => (m.id === id ? { ...m, [k]: v } : m)));

  const submit = async () => {
    setLoading(true);
    try {
      const qs = form?.questions || [];
      const dietQ = qs.find((q) => q.type === "allergies" || q.type === "dietary");
      const payload = { rsvp_status: status, answers };
      if (mealQ) payload.meal_choice = answers[mealQ.label] || "";
      if (dietQ) payload.dietary = answers[dietQ.label] || "";
      if (isHousehold) {
        payload.household_updates = members
          .filter((m) => m.id !== data.guest.id)
          .map((m) => ({ id: m.id, rsvp_status: m.rsvp_status, meal_choice: m.meal_choice, allergies: m.allergies }));
        // primary guest answers come from its own member row
        const primary = members.find((m) => m.id === data.guest.id);
        if (primary) {
          payload.rsvp_status = primary.rsvp_status;
          if (mealQ) payload.meal_choice = primary.meal_choice;
          if (primary.allergies) payload.allergies = primary.allergies;
          setStatus(primary.rsvp_status);
        }
      }
      await axios.post(`${API}/public/rsvp/${token}`, payload);
      setDone(true);
    } catch (e) { toast.error("Une erreur est survenue. Réessayez."); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="card-lux p-10 max-w-md text-center animate-fade-up" data-testid="rsvp-done">
        <div className="w-16 h-16 rounded-full bg-sage/15 flex items-center justify-center mx-auto mb-5">
          <PartyPopper className="w-7 h-7 text-sage" /></div>
        <h1 className="font-serif text-3xl">Merci !</h1>
        <p className="text-stone-600 mt-3">{w.names} ont bien reçu votre réponse.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="relative h-64 lg:h-80">
        <img src={HERO} alt="Fleurs de mariage" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-stone-900/10 flex items-end">
          <div className="max-w-2xl mx-auto w-full px-6 pb-8 text-white">
            <div className="eyebrow text-champagne mb-2">Vous êtes invité·e</div>
            <h1 className="font-serif text-4xl lg:text-5xl">{w.names}</h1>
            <p className="mt-2 opacity-90">{fdateShort(w.date)} · {w.city}</p>
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-6 py-10">
        <div className="card-lux p-8 animate-fade-up">
          <p className="text-stone-700 mb-1">Bonjour <b>{data.household_name || data.guest.name}</b>,</p>
          <p className="text-stone-500 text-sm mb-6">{form?.intro || "Merci de confirmer votre présence ci-dessous."}</p>

          {isHousehold ? (
            <div className="space-y-4" data-testid="household-rsvp">
              <div className="flex items-center gap-2 text-sm text-stone-500">
                <Users className="w-4 h-4" /> Répondez pour chaque membre de votre foyer.
              </div>
              {members.map((m) => (
                <div key={m.id} className="border rounded-2xl p-4" data-testid={`member-${m.id}`}>
                  <div className="font-medium mb-3">{m.name}</div>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <button type="button" data-testid={`member-yes-${m.id}`} onClick={() => setMember(m.id, "rsvp_status", "attending")}
                      className={`p-3 rounded-xl border text-left text-sm ${m.rsvp_status === "attending" ? "border-sage bg-sage/10" : "border-stone-200"}`}>
                      <Check className="w-4 h-4 text-sage mb-1" /> Présent·e</button>
                    <button type="button" data-testid={`member-no-${m.id}`} onClick={() => setMember(m.id, "rsvp_status", "declined")}
                      className={`p-3 rounded-xl border text-left text-sm ${m.rsvp_status === "declined" ? "border-destructive bg-destructive/5" : "border-stone-200"}`}>
                      <Heart className="w-4 h-4 text-stone-400 mb-1" /> Absent·e</button>
                  </div>
                  {m.rsvp_status === "attending" && mealQ && (
                    <div className="mb-2">
                      <Label className="text-xs">{mealQ.label}</Label>
                      <select value={m.meal_choice} onChange={(e) => setMember(m.id, "meal_choice", e.target.value)}
                        data-testid={`member-meal-${m.id}`} className="mt-1 h-10 w-full rounded-md border border-input px-3 bg-white">
                        <option value="">— Sélectionnez —</option>
                        {(mealQ.options || []).map((o) => <option key={o}>{o}</option>)}
                      </select>
                    </div>
                  )}
                  {m.rsvp_status === "attending" && (
                    <div>
                      <Label className="text-xs">Allergies / régime</Label>
                      <Input value={m.allergies} onChange={(e) => setMember(m.id, "allergies", e.target.value)}
                        className="mt-1 h-9" placeholder="Optionnel" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <>
              <Label className="mb-2 block">Serez-vous des nôtres ?</Label>
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button data-testid="rsvp-yes" onClick={() => setStatus("attending")}
                  className={`p-4 rounded-xl border text-left transition-all ${status === "attending" ? "border-sage bg-sage/10" : "border-stone-200"}`}>
                  <Check className="w-5 h-5 text-sage mb-1" /><div className="font-medium">Avec joie</div>
                  <div className="text-xs text-stone-500">Je serai présent·e</div></button>
                <button data-testid="rsvp-no" onClick={() => setStatus("declined")}
                  className={`p-4 rounded-xl border text-left transition-all ${status === "declined" ? "border-destructive bg-destructive/5" : "border-stone-200"}`}>
                  <Heart className="w-5 h-5 text-stone-400 mb-1" /><div className="font-medium">Malheureusement non</div>
                  <div className="text-xs text-stone-500">Je ne pourrai pas venir</div></button>
              </div>
              {status === "attending" && (
                <div className="space-y-4 animate-fade-up">
                  {(form?.questions || []).filter((q) => q.enabled !== false).map((q, i) => (
                    <div key={i}>
                      <Label>{q.label}{q.required && <span className="text-destructive"> *</span>}</Label>
                      {q.description && <p className="text-xs text-stone-400 mb-1">{q.description}</p>}
                      {(q.type === "meal" || q.type === "choice" || q.type === "dropdown") ? (
                        <select data-testid="rsvp-q" value={answers[q.label] || ""} onChange={(e) => setAnswers({ ...answers, [q.label]: e.target.value })}
                          className="mt-1.5 h-11 w-full rounded-md border border-input px-3 bg-white">
                          <option value="">— Sélectionnez —</option>
                          {(q.options || []).map((o) => <option key={o}>{o}</option>)}
                        </select>
                      ) : (q.type === "allergies" || q.type === "dietary" || q.type === "textarea") ? (
                        <Textarea data-testid="rsvp-q" value={answers[q.label] || ""} onChange={(e) => setAnswers({ ...answers, [q.label]: e.target.value })} className="mt-1.5" rows={2} />
                      ) : q.type === "yesno" ? (
                        <div className="flex gap-2 mt-1.5">
                          {["Oui", "Non"].map((o) => (
                            <button key={o} type="button" onClick={() => setAnswers({ ...answers, [q.label]: o })}
                              className={`px-4 py-2 rounded-full border text-sm ${answers[q.label] === o ? "bg-stone-900 text-white" : "bg-white"}`}>{o}</button>))}
                        </div>
                      ) : (
                        <Input data-testid="rsvp-q" type={q.type === "number" ? "number" : "text"} className="mt-1.5 h-11"
                          value={answers[q.label] || ""} onChange={(e) => setAnswers({ ...answers, [q.label]: e.target.value })} />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <Button data-testid="rsvp-submit" onClick={submit} disabled={loading} className="w-full h-12 rounded-full mt-8">
            {loading ? "Envoi…" : "Envoyer ma réponse"}</Button>
        </div>
      </div>
    </div>
  );
}
